import { Injectable, inject } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { Log } from '@app/core/store/actions/logger.actions';
import { Go } from '@app/core/store/actions/router.actions';
import * as fromRoot from '@app/store/reducers';
import { Auth2Service } from '@core/auth2/services/auth2.service';
import { FeatureFlagService } from '@core/feature-flags/feature-flag.service';
import { auth2Actions } from '@core/auth2/store/auth2.actions';
import { resetAutoLoginAttempts } from '@core/auth2/auto-login-attempts';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { Action, Store } from '@ngrx/store';
import { EMPTY, Observable, from, of, throwError, timer } from 'rxjs';
import { catchError, map, retry, switchMap, tap } from 'rxjs/operators';
import { Auth2States } from './auth2.reducer';
import { APP_CONFIG, AppConfig } from '@cco-apps/cco-frontend';
import { HttpClient, HttpErrorResponse } from '@angular/common/http';
import {
  ErrorDialogComponent,
  ErrorDialogData,
} from '@modules/error-dialog/components/error-dialog/error-dialog.component';
import { MatDialog } from '@angular/material/dialog';
import * as Sentry from '@sentry/angular';
import { rehydrateAction, rehydrateErrorAction } from '@core/ngrx/ngrx-store-idb/ngrx-store-idb.actions';
import { userProfileActions } from '@main/user/user-profile.actions';
import { concatLatestFrom } from '@ngrx/operators';
import { forgetSessionTenantId } from '@app/core/user-tenant/tenant-context';
import { clearCcoDatabaseContents } from '@app/core/user-tenant/tenant-db-cleanup';

function redirectUrl() {
  const redirectPath = location.href.replace(location.origin, '');
  if (redirectPath.startsWith('/app/auth2'))
    return null;
  return location.href;
}

@Injectable()
export class Auth2Effects {
  private readonly config = inject<AppConfig>(APP_CONFIG);
  private actions$ = inject(Actions);
  private auth2Service = inject(Auth2Service);
  private featureFlagService = inject(FeatureFlagService);
  private route = inject(ActivatedRoute);
  private router = inject(Router);
  private store = inject(Store);
  private http = inject(HttpClient);
  private dialog = inject(MatDialog);


  checkAuthenticationStatus$: Observable<Action> = createEffect(() =>
    { return this.actions$.pipe(
      ofType(
        rehydrateAction,
        rehydrateErrorAction,
        userProfileActions.refreshTokensSuccess
      ),
      switchMap(() =>
        this.auth2Service.checkAuthenticationStatus().pipe(
          map(user => auth2Actions.checkAuthenticationStatusSuccess({
            currentUser: user,
            redirectUrl: redirectUrl(),
          })),
          // A 500 here is most often the backend's database connection pool being
          // exhausted, which is exactly what releasing the gate immediately would
          // make worse: every guarded route would fan out on the remember-me
          // cookie alone and the backend would run a full login for each request.
          retry({
            count: 3,
            delay: (error, retryCount) => (error?.status === 500 || error?.status === 504)
              ? timer(retryCount * 2000)
              : throwError(() => error),
          }),
          catchError((error) => {
            // 504 - the backend is down (gateway error)
            // 500 - the backend is shutting down (database connection error)
            if (error && error.status && (error.status === 504 || error.status === 500)) {
              return of<Action>(
                auth2Actions.checkAuthenticationStatusTimeout({
                  redirectUrl: redirectUrl(),
                }),
              );
            } else {
              return of(
                auth2Actions.checkAuthenticationStatusFailure({ error }),
                Log({
                  level: 5,
                  source: `${ this.constructor.name }.checkAuthenticationStatus$`,
                  message: `You are not currently logged in`,
                  object: error,
                }),
              );
            }
          }),
        ),
      ),
    ); },
  );

  checkAuthenticationStatusSuccess$: Observable<Action> = createEffect(() =>
    { return this.actions$.pipe(
      ofType(auth2Actions.checkAuthenticationStatusSuccess),
      concatLatestFrom(() => this.store.select((fromRoot.selectAuth2StatePreviousState))),
      switchMap(([action, authState]) => {
        if (authState === Auth2States.AUTHENTICATING) {
          if (action.redirectUrl) {
            const redirectPath = action.redirectUrl.replace(location.origin, '');
            return of(Go({ path: [redirectPath] }));
          }

          return of(Go({ path: ['/app'] }));
        }

        if (action.redirectUrl) {
          if (location.href !== action.redirectUrl) {
            // For this to really work, we either need to call router.natigateByUrl or fully parse the URL
            const redirectPath = action.redirectUrl.replace(location.origin, '');
            // const splitRedirectPath = redirectPath.split(';');
            // const routeParams = {};
            // if (splitRedirectPath.slice(1).length > 0) {
            //   for (const split of splitRedirectPath.slice(1)) {
            //     const splitSplit = split.split('=');
            //     routeParams[splitSplit[0]] = splitSplit[1];
            //   }
            // }
            // return of(Go({ path: [splitRedirectPath[0], routeParams] }));
            this.router.navigateByUrl(redirectPath);
            return of(auth2Actions.authNullAction());
          }
        }

        return of(auth2Actions.authNullAction());
      }),
    ); },
  );

  checkAuthenticationStatusFailure$: Observable<Action> = createEffect(() => {
      return this.actions$.pipe(
        ofType(auth2Actions.checkAuthenticationStatusFailure),
        switchMap(() => of(auth2Actions.authenticateUser())),
      );
    },
  );

  listCurrentUserRoles$ = createEffect(() =>
    { return this.actions$.pipe(
      ofType(auth2Actions.checkAuthenticationStatusSuccess),
      switchMap(() => this.auth2Service.listCurrentUserRoles$().pipe(
        map(roles => auth2Actions.listCurrentUserRolesSuccess({ roles })),
        catchError(err => of(auth2Actions.listCurrentUserRolesFailure({ err }))),
      )),
    ); },
  );

  listCurrentUserRolesFailure$ = createEffect(() =>
    { return this.actions$.pipe(
      ofType(auth2Actions.listCurrentUserRolesFailure),
      switchMap(({ err }) => this.reauthenticateOrReportSystemError(
        err,
        'listCurrentUserRolesFailure$',
        'An error occurred while attempting to fetch the roles your user has been assigned in this tenant.',
      )),
    ); },
  );

  loadEnabledFeatureFlags$ = createEffect(() =>
    { return this.actions$.pipe(
      ofType(auth2Actions.checkAuthenticationStatusSuccess),
      switchMap(() => this.featureFlagService.listEnabledFeatureFlags$().pipe(
        map(enabledFeatureFlags => auth2Actions.loadEnabledFeatureFlagsSuccess({ enabledFeatureFlags })),
        catchError(err => of(auth2Actions.loadEnabledFeatureFlagsFailure({ err }))),
      )),
    ); },
  );

  loadEnabledFeatureFlagsFailure$ = createEffect(() =>
    { return this.actions$.pipe(
      ofType(auth2Actions.loadEnabledFeatureFlagsFailure),
      switchMap((err) => {
        const eventId = Sentry.captureException(err, {
          fingerprint: [
            'auth2.effects',
            'loadEnabledFeatureFlagsFailure$',
          ],
        });

        this.dialog.open<ErrorDialogComponent, ErrorDialogData>(ErrorDialogComponent, {
          data: {
            title: 'System Error',
            message: 'An error occurred while attempting to fetch the feature flags enabled for this tenant.',
            eventId,
          },
        });

        return EMPTY;
      })
    ); },
    { dispatch: false },
  );

  getCurrentTenantUser$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(auth2Actions.checkAuthenticationStatusSuccess),
      switchMap(() => this.auth2Service.getCurrentTenantUser$().pipe(
        map(tenantUser => auth2Actions.getCurrentTenantUserSuccess({ tenantUser })),
        catchError(err => of(auth2Actions.getCurrentTenantUserFailure({ err }))),
      )),
    );
  });

  getCurrentTenantUserFailure$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(auth2Actions.getCurrentTenantUserFailure),
      switchMap(({ err }) => this.reauthenticateOrReportSystemError(
        err,
        'getCurrentTenantUserFailure$',
        'An error occurred while attempting to fetch your Tenant User details.',
      )),
    );
  });

  /**
   * A 401 or 403 from a tenant-scoped call means the session has no account in this tenant (the
   * backend's NoTenantUserAuthenticationException contract): re-authenticating recreates it, so
   * that is not a system error to report.
   */
  private reauthenticateOrReportSystemError(err: unknown, effectName: string, message: string): Observable<Action> {
    if (err instanceof HttpErrorResponse && (err.status === 401 || err.status === 403))
      return of(auth2Actions.authenticateUser());

    const eventId = Sentry.captureException(err, {
      fingerprint: [
        'auth2.effects',
        effectName,
      ],
    });

    this.dialog.open<ErrorDialogComponent, ErrorDialogData>(ErrorDialogComponent, {
      data: {
        title: 'System Error',
        message,
        eventId,
      },
    });

    return EMPTY;
  }

  authenticateUser$: Observable<Action> = createEffect(() =>
    { return this.actions$.pipe(
      ofType(auth2Actions.authenticateUser),
      switchMap(() => {
        this.dialog.closeAll();

        if (
          this.route.snapshot.queryParams.authMethod &&
          this.route.snapshot.queryParams.authMethod === 'local'
        ) {
          return of(Go({ path: ['/app/auth2/login'] }));
        } else {
          return of(Go({ path: ['/app/auth2/authenticate'] }));
        }
      }),
    ); },
  );

  logout$ = createEffect(() =>
    { return this.actions$.pipe(
      ofType(auth2Actions.logout),
      switchMap(() => {
        localStorage.clear();
        forgetSessionTenantId();

        // Clear (not delete) the databases: a delete blocked by another
        // tab's open connection would stay pending and wedge every
        // subsequent open of that database - see tenant-db-cleanup.ts.
        return from(clearCcoDatabaseContents(1000)).pipe(
          catchError(() => of(undefined)),
          map(() => auth2Actions.remoteLogout()),
        );
      }),
    ); },
  );

  remoteLogout$ = createEffect(
    () =>
      { return this.actions$.pipe(
        ofType(auth2Actions.remoteLogout),
        switchMap(() => {
          location.href = `${this.config.apiBaseUrl}/security/logout`;
          return EMPTY;
        }),
      ); },
    { dispatch: false },
  );

  beginOAuth2LoginProcess$ = createEffect(
    () =>
      { return this.actions$.pipe(
        ofType(auth2Actions.beginOAuth2LoginProcess),
        concatLatestFrom(() => this.store.select((fromRoot.selectAuth2RedirectUrl))),
        switchMap(([action, redirectUrl]) => {
          return this.http.get(`${this.config.apiBaseUrl}/security/oauth2/tenant/${action.tenantCode}`).pipe(
            switchMap(() => {
              let href = `${this.config.apiBaseUrl}/oauth2/authorization/${action.tenantCode}?rememberMe=${action.rememberMe}`;
              if (redirectUrl) {
                href = `${href}&redirect_uri=${encodeURIComponent(redirectUrl)}`;
              }

              location.href = href;

              return EMPTY;
            }),
            catchError((err) => {
              if (err.status === 404) {
                if (!action.auto) {
                  this.dialog.open(ErrorDialogComponent, {
                    data: {
                      title: 'Invalid Tenant Code',
                      message: `'${action.tenantCode}' is not a valid tenant code.`
                    }
                  });
                }
              } else {
                Sentry.captureException(err);
              }
              return of(auth2Actions.beginOAuth2LoginProcessFailure({
                tenantCode: action.tenantCode,
                status: err.status,
              }));
            }),
          );
        }),
      ); },
  );

  resetAutoLoginAttempts$ = createEffect(
    () => {
      return this.actions$.pipe(
        ofType(auth2Actions.checkAuthenticationStatusSuccess),
        tap(() => resetAutoLoginAttempts()),
      );
    },
    { dispatch: false },
  );
}
